import { useAtomValue, useSetAtom } from 'jotai';
import { aiFeaturePermissionAtom } from '../components/chat-input/state';
import { useCallback, useRef } from 'react';
import { sendMessageToCache } from '../states/cache';
import { chatRowSetter } from '../states/chat';
import { reminderTriggerAtom } from '../states/reminder';
import { getTriggeredReminders } from '../requests/reminder';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import {
  Message,
  MessageOrigin,
  MessageTypesEnum,
  REMINDER_MESSAGE_STATUS,
  ReminderMessage,
  ReminderTriggerMessage,
} from '../typings/message-types';
import { CHAT_UPDATE_TYPE } from '../typings/chat';

dayjs.extend(utc);
dayjs.extend(timezone);

export default function useReminderFallbackPoll() {
  const featurePermissions = useAtomValue(aiFeaturePermissionAtom);
  const updateCache = useSetAtom(sendMessageToCache);
  const setChatRow = useSetAtom(chatRowSetter);
  const setReminderTrigger = useSetAtom(reminderTriggerAtom);

  const pollingInterval = featurePermissions?.pollingInterval?.reminders || 3000;
  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const isPollingRef = useRef(false);
  const lastCheckedAtRef = useRef(
    dayjs.utc(new Date().toISOString()).tz('Asia/Kolkata').format('YYYY-MM-DDTHH:mm:ss.SSSZ')
  );

  const poll = useCallback(async () => {
    try {
      const response = await getTriggeredReminders({
        lastCheckedAt: lastCheckedAtRef.current,
      });
      if (response?.reminders?.length) {
        response.reminders.forEach((payload) => {
          const reminderMessageToCache: Message = {
            id: payload.chatMessageId || '',
            message: {
              reminderId: payload.reminderId,
              messageType: MessageTypesEnum.REMINDER,
              reminderStatus: REMINDER_MESSAGE_STATUS.TRIGGERED,
              reminderMessage: payload.reminderMessage,
              assigneeDetails: payload.assigneeDetails,
              dueAt: payload.dueAt,
            } as ReminderMessage,
            repliesCount: 0,
            deliveryCount: 0,
            erroredCount: 0,
            sentCount: 0,
            replyMessage: undefined,
            messageTime: 0,
            senderId: '',
            messageOriginType: MessageOrigin.SYSTEM,
            messageMetadata: null,
            readCount: 0,
            senderUser: undefined,
            templateId: null,
            totalCount: 1,
            unreadCount: 0,
            noResponseCount: 0,
            awaitingTemplateApprovalCount: 0,
            errorMessage: '',
          };
          const reminderTriggerMessage: Message = {
            id: payload.reminderTriggerMessageId || '',
            message: {
              reminderId: payload.reminderId,
              messageType: MessageTypesEnum.REMINDER_TRIGGER,
              reminderStatus: REMINDER_MESSAGE_STATUS.TRIGGERED,
              reminderMessage: payload.reminderMessage,
              assigneeDetails: payload.assigneeDetails,
              dueAt: payload.dueAt,
            } as ReminderTriggerMessage,
            repliesCount: 0,
            deliveryCount: 0,
            erroredCount: 0,
            sentCount: 0,
            replyMessage: undefined,
            messageTime: 0,
            senderId: '',
            messageOriginType: MessageOrigin.SYSTEM,
            messageMetadata: null,
            readCount: 0,
            senderUser: undefined,
            templateId: null,
            totalCount: 1,
            unreadCount: 0,
            noResponseCount: 0,
            awaitingTemplateApprovalCount: 0,
            errorMessage: '',
          };
          updateCache({
            chatId: payload.chatId || '',
            message: reminderMessageToCache,
            partial: false,
            queue: false,
            fromSocket: true,
          });
          updateCache({
            chatId: payload.chatId || '',
            message: reminderTriggerMessage,
            partial: false,
            queue: false,
            fromSocket: true,
          });
          setChatRow({
            type: CHAT_UPDATE_TYPE.UPDATE,
            partial: true,
            data: {
              id: payload.chatId,
              isReminderTriggered: true,
            },
            reOrder: false,
          });
        });
        setReminderTrigger(() => ({
          open: true,
          reminderId: response.reminders[0].reminderId,
          chatId: response.reminders[0].chatId,
          reminderMessage: response.reminders[0].reminderMessage,
          dueAt: response.reminders[0].dueAt,
          assigneeDetails: response.reminders[0].assigneeDetails,
          chatMessageId: response.reminders[0].chatMessageId,
          createdBy: response.reminders[0].createdBy,
          chatTypeId: response.reminders[0].chatTypeId,
          createdByName: response.reminders[0].createdByName,
          phoneNumber: response.reminders[0].chat.phoneNumber,
          wabaNumber: response.reminders[0].chat.integrationWabaNumber,
          chatName: response.reminders[0].chat?.name || '',
        }));
      }
      lastCheckedAtRef.current = encodeURIComponent(
        dayjs.utc(response.lastCheckedAt).tz('Asia/Kolkata').format('YYYY-MM-DDTHH:mm:ss.SSSZ')
      );
    } catch (error) {
      console.error('Error fetching triggered reminders:', error);
    }
  }, []);

  const startPolling = useCallback(() => {
    if (isPollingRef.current) return;

    isPollingRef.current = true;

    poll();

    pollingIntervalRef.current = setInterval(async () => {
      poll();
    }, pollingInterval);
  }, []);

  const stopPolling = useCallback(() => {
    if (!isPollingRef.current) return;

    isPollingRef.current = false;

    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }
  }, []);

  return {
    startPolling,
    stopPolling,
  };
}
